import { LocalShipping as LocalShippingIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCargoCompanies } from "../../../hooks/useCargoCompany";
import { CargoCompany } from "../../../types/cargoCompany";
import { adminOrderService } from "../../../services/adminOrderService";

interface AssignCargoCompanyModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  currentCargoCompanyId?: string;
  currentCargoCompanyName?: string;
  hasShippedItems: boolean;
  onSuccess: () => void;
}

const AssignCargoCompanyModal: React.FC<AssignCargoCompanyModalProps> = ({
  open,
  onClose,
  orderId,
  currentCargoCompanyId,
  currentCargoCompanyName,
  hasShippedItems,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [selectedCargoCompanyId, setSelectedCargoCompanyId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: cargoCompanies, isLoading: loadingCompanies } = useCargoCompanies({
    activeOnly: true,
  });

  useEffect(() => {
    if (open) {
      setSelectedCargoCompanyId(currentCargoCompanyId || "");
      setError(null);
    }
  }, [open, currentCargoCompanyId]);

  const handleAssign = async () => {
    if (!selectedCargoCompanyId && currentCargoCompanyId) {
      // Unassigning
      setError(null);
      setLoading(true);
      try {
        await adminOrderService.assignCargoCompany(orderId, null);
        onSuccess();
        onClose();
      } catch (err: any) {
        setError(err.response?.data?.message || t("admin.assignCargo.failedToUnassign"));
      } finally {
        setLoading(false);
      }
    } else if (selectedCargoCompanyId) {
      // Assigning or changing
      setError(null);
      setLoading(true);
      try {
        await adminOrderService.assignCargoCompany(orderId, selectedCargoCompanyId);
        onSuccess();
        onClose();
      } catch (err: any) {
        setError(err.response?.data?.message || t("admin.assignCargo.failedToAssign"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedCargoCompanyId(currentCargoCompanyId || "");
      setError(null);
      onClose();
    }
  };

  const isChanging = currentCargoCompanyId && selectedCargoCompanyId !== currentCargoCompanyId;
  const isUnassigning = currentCargoCompanyId && !selectedCargoCompanyId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalShippingIcon />
          <Typography variant="h6">{t("admin.assignCargo.title")}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {hasShippedItems && (isChanging || isUnassigning) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("admin.assignCargo.shippedItemsWarning")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>{t("admin.assignCargo.cargoCompany")}</InputLabel>
          <Select
            value={selectedCargoCompanyId}
            onChange={(e) => setSelectedCargoCompanyId(e.target.value)}
            label={t("admin.assignCargo.cargoCompany")}
            disabled={loading || loadingCompanies}
          >
            <MenuItem value="">
              <em>{t("admin.assignCargo.unassigned")}</em>
            </MenuItem>
            {cargoCompanies?.data?.map((company: CargoCompany) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {currentCargoCompanyName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t("admin.assignCargo.current")}: {currentCargoCompanyName}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={loading || loadingCompanies || selectedCargoCompanyId === currentCargoCompanyId}
        >
          {loading ? t("admin.assignCargo.processing") : isUnassigning ? t("admin.assignCargo.unassign") : t("admin.assignCargo.assign")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignCargoCompanyModal;

